import React, { useState } from 'react';
import api from '../utils/api';
import { useLibrary } from '../context/LibraryContext';
import { FaYoutube, FaSearch, FaDownload, FaSpinner } from 'react-icons/fa';
import '../styles/YouTube.css';

const YouTube = () => {
  const { downloadFromYoutube, loading, error } = useLibrary();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [downloadForm, setDownloadForm] = useState({
    url: '',
    title: '',
    artist: '',
    album: '',
    genre: '',
    tags: ''
  });
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) {
      return;
    }
    
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      const res = await api.get(`/youtube/search?q=${encodeURIComponent(searchTerm)}`);
      setSearchResults(res.data);
    } catch (err) {
      console.error('Error searching YouTube:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectVideo = (video) => {
    setSelectedVideo(video);
    setDownloadForm({
      url: `https://www.youtube.com/watch?v=${video.id}`,
      title: video.title,
      artist: video.channelTitle,
      album: '',
      genre: '',
      tags: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDownloadForm({
      ...downloadForm,
      [name]: value
    });
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    setDownloadError('');
    setDownloadSuccess(false);
    
    try {
      // Simulate download progress
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 500);
      
      // Download from YouTube
      const result = await downloadFromYoutube(downloadForm);
      
      clearInterval(progressInterval);
      setDownloadProgress(100);
      setDownloadSuccess(true);
      
      // Reset form after successful download
      setTimeout(() => {
        setSelectedVideo(null);
        setDownloadForm({
          url: '',
          title: '',
          artist: '',
          album: '',
          genre: '',
          tags: ''
        });
        setDownloadProgress(0);
      }, 2000);
      
    } catch (err) {
      setDownloadError('Erreur lors du téléchargement: ' + (err.message || 'Erreur inconnue'));
      setDownloadProgress(0);
    }
  };

  const formatDuration = (duration) => {
    // Convert ISO 8601 duration to minutes:seconds
    if (!duration) return '0:00';
    
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    
    const hours = (match[1] ? parseInt(match[1].slice(0, -1)) : 0);
    const minutes = (match[2] ? parseInt(match[2].slice(0, -1)) : 0);
    const seconds = (match[3] ? parseInt(match[3].slice(0, -1)) : 0);
    
    const totalMinutes = hours * 60 + minutes;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
    
    return `${totalMinutes}:${formattedSeconds}`;
  };

  return (
    <div className="youtube-page">
      <div className="youtube-header">
        <h1><FaYoutube className="youtube-icon" /> Import YouTube</h1>
        <p>Recherchez et importez de la musique depuis YouTube</p>
      </div>
      
      <form onSubmit={handleSearch} className="youtube-search-form">
        <div className="search-input-container">
          <input
            type="text"
            placeholder="Rechercher sur YouTube..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            required
          />
          <button type="submit" className="btn-search" disabled={isSearching}>
            {isSearching ? <FaSpinner className="spinner" /> : <FaSearch />}
          </button>
        </div>
      </form>
      
      {isSearching ? (
        <div className="loading-results">Recherche en cours...</div>
      ) : searchResults.length > 0 ? (
        <div className="search-results">
          {searchResults.map(video => (
            <div 
              key={video.id} 
              className={`video-item ${selectedVideo?.id === video.id ? 'selected' : ''}`}
              onClick={() => handleSelectVideo(video)}
            >
              <div className="video-thumbnail">
                <img src={video.thumbnail} alt={video.title} />
                <span className="video-duration">{formatDuration(video.duration)}</span>
              </div>
              <div className="video-info">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-channel">{video.channelTitle}</p>
              </div>
            </div>
          ))}
        </div>
      ) : searchTerm && !isSearching ? (
        <div className="no-results">Aucun résultat trouvé pour "{searchTerm}"</div>
      ) : null}
      
      {selectedVideo && (
        <div className="download-panel">
          <h2>Télécharger "{selectedVideo.title}"</h2>
          
          <form onSubmit={handleDownload} className="download-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="title">Titre *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={downloadForm.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="artist">Artiste</label>
                <input
                  type="text"
                  id="artist"
                  name="artist"
                  value={downloadForm.artist}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="album">Album</label>
                <input
                  type="text"
                  id="album"
                  name="album"
                  value={downloadForm.album}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="genre">Genre</label>
                <input
                  type="text"
                  id="genre"
                  name="genre"
                  value={downloadForm.genre}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="tags">Tags (séparés par des virgules)</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={downloadForm.tags}
                  onChange={handleInputChange}
                  placeholder="ex: chill, été, acoustique"
                />
              </div>
            </div>
            
            <button 
              type="submit" 
              className="btn-download"
              disabled={loading}
            >
              {loading ? <FaSpinner className="spinner" /> : <FaDownload />} 
              Télécharger
            </button>
          </form>
          
          {downloadProgress > 0 && (
            <div className="download-progress-container">
              <div 
                className="download-progress-bar" 
                style={{ width: `${downloadProgress}%` }}
              ></div>
              <span className="download-progress-text">{downloadProgress}%</span>
            </div>
          )}
          
          {downloadSuccess && (
            <div className="download-success">
              Téléchargement réussi !
            </div>
          )}
          
          {(downloadError || error) && (
            <div className="download-error">
              {downloadError || error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YouTube;
